import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ServicesManifesto from "@/components/ServicesManifesto";
import EditorialMarquee from "@/components/EditorialMarquee";
import FaqBlock from "@/components/FaqBlock";
import Seo from "@/components/Seo";
import { INDUSTRIES, SERVICES_LIST } from "@/data/services";
import { SERVICES_FAQS } from "@/data/faqs";
import { faqSchema, professionalServiceSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

export default function ServicesPage() {
  return (
    <div>
      <Seo
        title="Services — Recruitment, HR Consulting, Employer Branding"
        description="Five primary practices for growing IT and non-IT companies: Recruitment, HR Consulting, HR Operations, Employer Branding and GCC Support — with fractional HR services and HR Tech Consulting delivered under HR Consulting. Bengaluru based, delivered globally across the US, UK, Canada, India, Poland, Spain and Ukraine."
        path="/services"
        jsonLd={[professionalServiceSchema(), faqSchema(SERVICES_FAQS)]}
      />
      {/* Page header */}
      <section className="pt-40 pb-16 md:pb-24 bg-[var(--bg)] border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="overline mb-6">Services · Five primary practices</p>
          </div>
          <div className="md:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE }}
              className="font-display text-5xl md:text-7xl lg:text-8xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
            >
              What we do.{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                Precisely.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: EASE }}
              className="mt-8 text-[var(--ink-80)] text-lg leading-relaxed max-w-2xl"
            >
              A senior HR practice for IT and non-IT companies — five clearly
              scoped primary practices. Engage them one at a time, or combine
              them under a fractional HR arrangement.
            </motion.p>

            {/* Practice quick-links */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-3">
              {SERVICES_LIST.map((s) => (
                <Link
                  key={s.id}
                  to={s.id === "gcc-support" ? "/gcc-support" : `/services/${s.id}`}
                  className="border border-[var(--line-strong)] p-4 hover:bg-[var(--bg-2)] transition-colors"
                  data-testid={`service-link-${s.id}`}
                >
                  <span className="font-serif-accent italic text-[var(--accent)] text-lg block mb-2">
                    {s.number}
                  </span>
                  <span className="font-display text-base text-[var(--ink)] leading-tight block">
                    {s.title}
                  </span>
                </Link>
              ))}
            </div>

            <div className="mt-10">
              <Link to="/contact" className="btn-pill">
                <span>Talk about your context</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee items={INDUSTRIES} speed={24} />
      <ServicesManifesto showIntro={false} />
      <FaqBlock
        kicker="Services · FAQ"
        title="Details that come up on every discovery call."
        items={SERVICES_FAQS}
      />
    </div>
  );
}
